import React, { useState, useCallback } from 'react';
import { Card, Statistic, Typography, Button, Space, Modal, Tooltip, Tag, Drawer } from 'antd';
import { 
  ArrowUpOutlined, ArrowDownOutlined, EditOutlined, InfoCircleOutlined,
  LineChartOutlined, BarChartOutlined, EyeOutlined, HistoryOutlined
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

const { Text, Title } = Typography;

const EnhancedMetricCard = ({ 
  metric, 
  data, 
  isMobile = false, 
  onEdit, 
  historicalData = [],
  showComparison = false,
  comparisonData = {},
  isEditable = false,
  cacheInfo = null
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const getMetricStyle = (metric) => {
    const metricStyles = {
      'REVENUE': { 
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        icon: '💰',
        color: '#667eea'
      },
      'ROI': { 
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        icon: '📈',
        color: '#f093fb'
      },
      'COST(ALL)': { 
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        icon: '💳',
        color: '#4facfe'
      },
      'COS': { 
        gradient: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
        icon: '💹',
        color: '#fad0c4'
      },
      'META COST (ALL)': { 
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        icon: '📱',
        color: '#43e97b'
      },
      'BOOST ADS': { 
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        icon: '🚀',
        color: '#fa709a'
      },
      'INSTAGRAM FOLLOWER': { 
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        icon: '👥',
        color: '#a8edea'
      },
      'TRANSACTION': { 
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
        icon: '🛒',
        color: '#ffecd2'
      },
      'AVARAGE ORDER VALUE': { 
        gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
        icon: '💎',
        color: '#a18cd1'
      }
    };
    
    return metricStyles[metric] || {
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: '📊',
      color: '#667eea'
    };
  };

  const formatValue = (value, metric) => {
    if (!value && value !== 0) return 'N/A';
    
    if (metric === 'REVENUE' || metric.includes('COST')) {
      if (isMobile && value > 1000000) {
        return `${(value / 1000000).toFixed(1)}M ₺`;
      } else if (isMobile && value > 1000) {
        return `${(value / 1000).toFixed(0)}K ₺`;
      }
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('tr-TR').format(value);
  };

  const getDataSource = useCallback(() => {
    if (cacheInfo?.cached) return 'cache';
    if (data?.isManual) return 'manual';
    if (data?.isCalculated) return 'calculated';
    return 'api';
  }, [cacheInfo, data]);

  const getDataSourceInfo = () => {
    const source = getDataSource();
    const sourceMap = {
      'cache': { color: 'blue', text: 'Önbellek', icon: '⚡' },
      'manual': { color: 'orange', text: 'Manuel', icon: '✏️' },
      'calculated': { color: 'green', text: 'Hesaplanmış', icon: '🧮' },
      'api': { color: 'cyan', text: 'API', icon: '🔗' }
    };
    return sourceMap[source] || sourceMap.api;
  };

  const style = getMetricStyle(metric);
  const sourceInfo = getDataSourceInfo();

  const handleCardClick = () => {
    if (!isMobile) {
      setShowDetails(true);
    }
  };

  const renderMiniChart = () => {
    if (!historicalData || historicalData.length === 0) return null;

    return (
      <ResponsiveContainer width="100%" height={60}>
        <LineChart data={historicalData}>
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke="rgba(255,255,255,0.8)" 
            strokeWidth={2}
            dot={false}
          />
          <RechartsTooltip 
            contentStyle={{ 
              backgroundColor: 'rgba(0,0,0,0.8)', 
              border: 'none', 
              borderRadius: '4px',
              fontSize: '12px'
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderDetailModal = () => (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: '20px' }}>{style.icon}</span>
          <Title level={4} style={{ margin: 0 }}>{metric} Detayları</Title>
        </Space>
      }
      open={showDetails}
      onCancel={() => setShowDetails(false)}
      footer={[
        <Button key="close" onClick={() => setShowDetails(false)}>
          Kapat
        </Button>,
        isEditable && (
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={onEdit}>
            Düzenle
          </Button>
        )
      ].filter(Boolean)}
      width={isMobile ? '95%' : 600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Ana Metrikler */}
        <Card size="small">
          <Statistic
            title="Mevcut Değer"
            value={data.current}
            formatter={(value) => formatValue(value, metric)}
            valueStyle={{ fontSize: '24px', color: style.color }}
          />
          {data.change !== 0 && (
            <div style={{ marginTop: 8 }}>
              <Tag color={data.change > 0 ? 'green' : 'red'}>
                {data.change > 0 ? '+' : ''}{data.change.toFixed(1)}% değişim
              </Tag>
            </div>
          )}
        </Card>

        {/* Karşılaştırma */}
        {showComparison && comparisonData && Object.keys(comparisonData).length > 0 && (
          <Card size="small" title={`Karşılaştırma - ${comparisonData.periodLabel || 'Önceki Dönem'}`}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>{comparisonData.periodLabel}: </Text>
                <Text strong>{formatValue(comparisonData.previous, metric)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Değişim: </Text>
                <Tag color={comparisonData.change > 0 ? 'green' : 'red'}>
                  {comparisonData.change > 0 ? '+' : ''}{comparisonData.change?.toFixed(1)}%
                </Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Hedef: </Text>
                <Text strong style={{ color: data.current >= comparisonData.target ? '#52c41a' : '#ff4d4f' }}>
                  {formatValue(comparisonData.target, metric)}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Sektör Ort.: </Text>
                <Text>{formatValue(comparisonData.benchmark, metric)}</Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Veri Kaynağı */}
        <Card size="small" title="Veri Bilgileri">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Kaynak: </Text>
              <Tag color={sourceInfo.color}>
                {sourceInfo.icon} {sourceInfo.text}
              </Tag>
            </div>
            {cacheInfo && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Son Güncelleme: {new Date(cacheInfo.timestamp).toLocaleString('tr-TR')}
              </Text>
            )}
            <Text type="secondary">
              Toplam: {formatValue(data.total, metric)} | 
              Ortalama: {formatValue(data.average, metric)}
            </Text>
          </Space>
        </Card>

        {/* Tarihsel Grafik */}
        {historicalData && historicalData.length > 0 && (
          <Card size="small" title="Son 30 Gün Trendi">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={historicalData}>
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke={style.color} 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </Space>
    </Modal>
  );

  return (
    <>
      <Card 
        hoverable
        size={isMobile ? "small" : "default"}
        onClick={handleCardClick}
        style={{ 
          background: style.gradient,
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minHeight: isMobile ? '140px' : '160px',
          cursor: isMobile ? 'default' : 'pointer',
          transition: 'all 0.3s ease'
        }}
        styles={{
          body: { 
            padding: isMobile ? '12px' : '16px',
            position: 'relative'
          }
        }}
      >
        {/* Veri Kaynağı Badge */}
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <Tooltip title={`Kaynak: ${sourceInfo.text}`}>
            <Tag 
              size="small" 
              color={sourceInfo.color}
              style={{ 
                margin: 0, 
                fontSize: '10px',
                borderRadius: '8px',
                opacity: 0.9
              }}
            >
              {sourceInfo.icon}
            </Tag>
          </Tooltip>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: isMobile ? '18px' : '22px', marginRight: 8 }}>{style.icon}</span>
          <Text style={{ 
            color: 'rgba(255,255,255,0.95)', 
            fontSize: isMobile ? '12px' : '14px', 
            fontWeight: '600',
            flex: 1
          }}>
            {metric}
          </Text>
        </div>

        {/* Ana Değer */}
        <div style={{ marginBottom: historicalData?.length > 0 ? 8 : 16 }}>
          <Statistic
            value={data.current}
            precision={metric === 'ROI' ? 2 : metric.includes('COST') || metric === 'REVENUE' ? 0 : 2}
            formatter={(value) => formatValue(value, metric)}
            valueStyle={{ 
              color: 'white', 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 'bold',
              lineHeight: '1.1'
            }}
            prefix={
              data.trend === 'up' ? 
                <ArrowUpOutlined style={{ color: '#52c41a', fontSize: isMobile ? '14px' : '18px' }} /> : 
              data.trend === 'down' ? 
                <ArrowDownOutlined style={{ color: '#ff4d4f', fontSize: isMobile ? '14px' : '18px' }} /> : null
            }
          />
        </div>

        {/* Mini Chart */}
        {!isMobile && historicalData && historicalData.length > 0 && (
          <div style={{ height: '60px', marginTop: 'auto' }}>
            {renderMiniChart()}
          </div>
        )}

        {/* Alt Bilgiler */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: 'auto'
        }}>
          {data.change !== 0 && (
            <Tag 
              color={data.change > 0 ? 'success' : 'error'}
              style={{ 
                margin: 0,
                fontSize: isMobile ? '10px' : '11px',
                borderRadius: '6px'
              }}
            >
              {data.change > 0 ? '+' : ''}{data.change.toFixed(1)}%
            </Tag>
          )}
          
          <Space size="small">
            {!isMobile && (
              <Tooltip title="Detayları Görüntüle">
                <Button 
                  type="text" 
                  size="small"
                  icon={<EyeOutlined />}
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetails(true);
                  }}
                />
              </Tooltip>
            )}
            
            {isEditable && (
              <Tooltip title="Düzenle">
                <Button 
                  type="text" 
                  size="small"
                  icon={<EditOutlined />}
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(metric, data);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        </div>
      </Card>

      {/* Detail Modal */}
      {renderDetailModal()}
    </>
  );
};

export default React.memo(EnhancedMetricCard);